import React, { useEffect, useState, useContext, useCallback } from "react";
import { SocketContext } from "../App";
import { useTranslation } from "react-i18next";
import {
  Navbar,
  ScrollArea,
  createStyles,
  Code,
  UnstyledButton,
  Image,
  Badge,
  Text,
  Group,
  ActionIcon,
  Tooltip,
  TextInput,
  useMantineColorScheme,
  useMantineTheme,
  Switch,
  Header,
  MediaQuery,
  Title,
  Burger,
  Divider,
  Stack,
} from "@mantine/core";
import { mainLinks, functionLinks } from "../data/navLinks";
import { IconRefresh } from "@tabler/icons";
import SystemClock from "./systemClock";
import _ from "lodash";

import { useSignOut, useAuthUser } from "react-auth-kit";
import { useTabNavigate } from "../hooks/useTabNavigate";
import UserPopupButton from "./userPopupButton";
import FrontierHeader from "../components/header";
import ReactJson from "react-json-view";
import { useForceUpdate } from "@mantine/hooks";
import SearchBox from "./searchBox";
import CompanyWorkspaceSwitcher from "./companyWorkspaceSwitcher";
import { useNotificationInbox } from "../hooks/useNotificationInbox";
import { useClientVersionCheck } from "../hooks/useClientVersionCheck";

const useStyles = createStyles((theme) => ({
  navbar: {
    paddingTop: 0,
    backgroundColor:
      theme.colorScheme === "dark" ? undefined : theme.white,
    borderRight:
      theme.colorScheme === "dark"
        ? undefined
        : `1px solid ${theme.colors.gray[4]}`,
  },

  section: {
    marginLeft: -theme.spacing.md,
    marginRight: -theme.spacing.md,
    marginBottom: theme.spacing.md,

    "&:not(:last-of-type)": {
      borderBottom: `1px solid ${
        theme.colorScheme === "dark"
          ? theme.colors.dark[4]
          : theme.colors.gray[3]
      }`,
    },
  },

  searchCode: {
    fontWeight: 700,
    fontSize: 10,
    backgroundColor:
      theme.colorScheme === "dark"
        ? theme.colors.dark[7]
        : theme.colors.gray[0],
    border: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[2]
    }`,
  },

  mainLinks: {
    paddingLeft: theme.spacing.md - theme.spacing.xs,
    paddingRight: theme.spacing.md - theme.spacing.xs,
    paddingBottom: theme.spacing.md,
  },

  mainLink: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    fontSize: theme.fontSizes.xs,
    padding: `8px ${theme.spacing.xs}px`,
    borderRadius: theme.radius.sm,
    fontWeight: 500,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[0]
        : theme.colors.gray[7],

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
      color: theme.colorScheme === "dark" ? theme.white : theme.black,
    },
  },

  mainLinkInner: {
    display: "flex",
    alignItems: "center",
    flex: 1,
  },

  mainLinkIcon: {
    marginRight: theme.spacing.sm,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[2]
        : theme.colors.dark[5],
  },

  collectionIcons: {
    marginRight: theme.spacing.sm,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[2]
        : theme.colors.gray[6],
  },

  mainLinkBadge: {
    padding: 0,
    width: 20,
    height: 20,
    pointerEvents: "none",
  },

  collections: {
    paddingLeft: theme.spacing.md - 6,
    paddingRight: theme.spacing.md - 6,
    paddingBottom: theme.spacing.md,
  },

  collectionsHeader: {
    paddingLeft: theme.spacing.md + 2,
    paddingRight: theme.spacing.md,
    marginBottom: 5,
  },

  collectionLink: {
    display: "block",
    padding: `8px ${theme.spacing.xs}px`,
    textDecoration: "none",
    borderRadius: theme.radius.sm,
    fontSize: theme.fontSizes.xs,
    color:
      theme.colorScheme === "dark"
        ? theme.colors.dark[0]
        : theme.colors.dark[5],
    lineHeight: 1,
    fontWeight: 500,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
      color: theme.colorScheme === "dark" ? theme.white : theme.black,
    },
  },

  header: {
    borderBottom: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[3]
    }`,
    paddingTop: theme.spacing.md,
  },

  footer: {
    // borderTop: `1px solid ${
    //     theme.colorScheme === "dark"
    //         ? theme.colors.dark[4]
    //         : theme.colors.gray[3]
    // }`,
    // paddingTop: theme.spacing.md,
  },
}));

const linkIdsByGroup = functionLinks.reduce((map, page) => {
  if (!page.group) return map;
  if (!map[page.group]) map[page.group] = new Set();
  map[page.group].add(page.linkId);
  return map;
}, {});

const countGrantedInGroup = (group, userRole) => {
  const linkIds = linkIdsByGroup[group];
  if (!linkIds) return 0;
  return (userRole?.links ?? []).filter(
    (entry) => linkIds.has(entry.link) && (entry.accessRight ?? 0) > 0
  ).length;
};

const dedupeNavLinksByPath = (links, userRole) => {
  const winners = new Map();
  for (const link of links) {
    const existing = winners.get(link.link);
    if (!existing) {
      winners.set(link.link, link);
      continue;
    }
    if (
      countGrantedInGroup(link.group, userRole) >
      countGrantedInGroup(existing.group, userRole)
    ) {
      winners.set(link.link, link);
    }
  }

  const emitted = new Set();
  return links.filter((link) => {
    if (winners.get(link.link) !== link) return false;
    if (emitted.has(link.link)) return false;
    emitted.add(link.link);
    return true;
  });
};

function FNavBar({ hidden, width, setOpened }) {
  const { classes } = useStyles();
  const auth = useAuthUser();
  const socket = useContext(SocketContext);
  const [openingTicketCount, setOpeningTicketCount] = useState(0);
  const { userRole } = auth();
  const tabNav = useTabNavigate();
  const forceUpdate = useForceUpdate();
  const { t } = useTranslation();
  const { unreadCount } = useNotificationInbox();
  const { updateAvailable, reload } = useClientVersionCheck();

  const hardRefresh = useCallback(() => {
    reload();
  }, [reload]);

  const sendSocketgetOpenTicketCount = async () => {
    try {
      if (!socket) throw new Error("No Socket Connection");
      const data = {
        userId: auth().id,
      };

      socket.emit("getOpenTicketCount", data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    sendSocketgetOpenTicketCount();

    socket.on("updateOpenTicketCount", (count) => {
      // console.log("updateOpenTicketCount", count);
      setOpeningTicketCount(count);
      const l = functionLinks.find((l) => l.link === "/chat");
      if (l.notifications === count) return;

      l.notifications = count;
      forceUpdate();
    });

    socket.on("needUpdateCount", () => {
      // console.log("needUpdateCount");
      sendSocketgetOpenTicketCount();
    });

    return () => {
      socket.off("updateOpenTicketCount");
      socket.off("needUpdateCount");
    };
  }, []);

  const grantedLinks = (userRole?.links ?? []).filter(
    (entry) => (entry.accessRight ?? 0) > 0
  );

  const availableFunctionLink = dedupeNavLinksByPath(
    functionLinks
      .filter(
        (l) =>
          l.alwaysShow ||
          grantedLinks.some(
            (roleLink) => roleLink.link === l.linkId
          )
      )
      ?.filter((l) => l.showOnNavBar === true),
    userRole
  );

  const renderLinks = availableFunctionLink.map((link, index) => {
    const badgeCount =
      link.link === "/notifications" ? unreadCount : link.notifications;
    const navKey = `${link.group ?? ""}::${link.link}`;

    return (
      <span key={navKey}>
        {(index === 0 ||
          link.group !== availableFunctionLink[index - 1].group) &&
          link.group && (
            <Text size={"xs"} weight={500} mt="xl" mb={"xs"}>
              {t(link.group)}
            </Text>
          )}
        <UnstyledButton
          key={navKey}
          className={classes.mainLink}
          onClick={() =>
            tabNav.open({ pathname: link.link, label: link.label })
          }
          h={35}
        >
          <div className={classes.mainLinkInner}>
            <link.icon
              size={20}
              className={classes.mainLinkIcon}
              stroke={1.5}
            />
            <a
              className={classes.collectionLink}
              href={link.link}
              onClick={(event) => event.preventDefault()}
            >
              <span>{t(link.label)}</span>
            </a>
          </div>
          {badgeCount != null && badgeCount > 0 && (
            <Badge size="sm" variant="filled" className={classes.mainLinkBadge}>
              {badgeCount > 99 ? "99+" : badgeCount}
            </Badge>
          )}
        </UnstyledButton>
        {link.showDivider && <Divider mt={"md"}></Divider>}
      </span>
    );
  });

  return (
    <>
      <Navbar
        p="md"
        hiddenBreakpoint="lg"
        hidden={hidden}
        width={width}
        className={classes.navbar}
        zIndex={1000}
      >
        {/* <ReactJson src={userRole.links} style={{ background: "white" }} /> */}
        <Navbar.Section className={classes.section}>
          <div className={classes.mainLinks}>
            <Stack spacing={0} mx="md">
              <Title order={2} lineClamp={1} sx={{ lineHeight: 1.15 }}>
                {process.env.REACT_APP_NAME}
              </Title>
              <Text size="11px" lineClamp={1} mt={2}>
                {t(process.env.REACT_APP_SUBTITLE)}
              </Text>
              <Group spacing="xs" noWrap mt="10px">
                <SystemClock />
                <Tooltip
                  label={
                    updateAvailable
                      ? t("app.updateAvailable.badgeTooltip", {
                          defaultValue:
                            "New version available — click to reload",
                        })
                      : "Refresh"
                  }
                  withArrow
                  position="bottom"
                  zIndex={1000}
                  multiline
                  width={220}
                >
                  <Code
                    sx={(theme) => ({
                      fontWeight: 700,
                      cursor: "pointer",
                      ...(updateAvailable
                        ? {
                            backgroundColor:
                              theme.colorScheme === "dark"
                                ? theme.colors.orange[9]
                                : theme.colors.orange[1],
                            color:
                              theme.colorScheme === "dark"
                                ? theme.colors.orange[0]
                                : theme.colors.orange[9],
                            animation: "pulse 2s ease-in-out infinite",
                            "@keyframes pulse": {
                              "0%, 100%": { opacity: 1 },
                              "50%": { opacity: 0.7 },
                            },
                          }
                        : {}),
                    })}
                    onClick={hardRefresh}
                  >
                    <Group spacing={4} noWrap>
                      {process.env.REACT_APP_VERSION}
                      <ActionIcon title="Refresh" size={16}>
                        <IconRefresh />
                      </ActionIcon>
                    </Group>
                  </Code>
                </Tooltip>
              </Group>
              <CompanyWorkspaceSwitcher />
            </Stack>
          </div>
        </Navbar.Section>

        <Navbar.Section className={classes.section} grow component={ScrollArea}>
          <SearchBox />

          <div className={classes.mainLinks}>{renderLinks}</div>
        </Navbar.Section>

        <Navbar.Section className={classes.footer}>
          <UserPopupButton user={auth()} />
        </Navbar.Section>
      </Navbar>
    </>
  );
}

export default React.memo(FNavBar);
